export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolves {@link promise} unless {@link timeoutMs} elapses first.
 */
export const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  message = 'Operation timed out',
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

type WaitUntilDefinedOptions = {
  timeoutMs: number;
  intervalMs?: number;
};

/**
 * Polls `getValue` until it returns a defined result or `timeoutMs` elapses.
 * Resolves with the value, or `undefined` on timeout.
 */
export const waitUntilDefined = async <T>(
  getValue: () => T | undefined,
  options: WaitUntilDefinedOptions,
): Promise<T | undefined> => {
  const intervalMs = options.intervalMs ?? 50;
  const deadline = Date.now() + options.timeoutMs;
  let value = getValue();
  while (value === undefined && Date.now() < deadline) {
    await sleep(intervalMs);
    value = getValue();
  }
  return value;
};

/**
 * Run {@link task} after {@link previous} settles. A rejected previous job
 * does not skip the next one. The returned promise is the task result, so
 * callers can handle failure. Use {@link enqueueAsync} when the queue must
 * never reject (click/save loops).
 */
export const chainAsync = <T>(
  previous: Promise<unknown>,
  task: () => Promise<T>,
): Promise<T> => previous.then(task, task);

/**
 * Runs async tasks one at a time. A rejected task does not skip the next one.
 * The returned promise is that task's own result.
 */
export const createAsyncQueue = (): (<T>(
  task: () => Promise<T>,
) => Promise<T>) => {
  let previous: Promise<unknown> = Promise.resolve();
  return (task) => {
    const result = chainAsync(previous, task);
    previous = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };
};

/**
 * Run {@link task} after {@link previous} settles. A rejected previous job
 * does not skip the next one. The returned promise rejects when {@link task}
 * fails so callers can distinguish success from failure. Attach a no-op
 * catch so fire-and-forget queues do not surface an unhandled rejection.
 */
export const enqueueAsync = (
  previous: Promise<unknown>,
  task: () => Promise<void>,
): Promise<void> => {
  const result = chainAsync(previous, task);
  result.catch(() => undefined);
  return result;
};

/**
 * Maps {@link items} with {@link run}, running the first item of each
 * {@link keyOf} group before the rest of that group. A provider prompt cache
 * only holds a prefix after one request with it has been processed, so the
 * first call warms the cache for its siblings. Groups run concurrently and
 * results keep input order.
 */
export const mapWithCacheWarmup = async <T, R>(
  items: readonly T[],
  keyOf: (item: T) => string,
  run: (item: T) => Promise<R>,
): Promise<R[]> => {
  const groups = new Map<string, { item: T; index: number }[]>();
  items.forEach((item, index) => {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), { item, index }]);
  });
  const results: R[] = new Array<R>(items.length);
  const runInto = async (entry: { item: T; index: number }): Promise<void> => {
    results[entry.index] = await run(entry.item);
  };
  await Promise.all(
    [...groups.values()].map(async ([head, ...tail]) => {
      if (head) await runInto(head);
      await Promise.all(tail.map(runInto));
    }),
  );
  return results;
};

export const objectPromiseAll = async <T extends object>(obj: T) => {
  const ops = await Promise.all(
    Object.entries(obj).map(async ([k, p]) => [k, await p]),
  );
  return Object.fromEntries(ops) as {
    [P in keyof T]: Awaited<T[P]>;
  };
};
